import dayjs, { Dayjs } from 'dayjs';
import { TFunction } from 'i18next';
import {
  TimeRangeEdge,
  TimeRangeEdgeEnum
} from '@models/time-range-picker.model.ts';
import {
  MinutesRange,
  ScheduleLegendEnum
} from '@modules/digital-signage/model/playlist-schedule.model.ts';
import { DAY_MINUTES } from '@modules/digital-signage/utils/schedule.utils.ts';

/** Minutes from 00:00. As a range end, 00:00 means the end of the day (1440). */
export const toMinutes = (value: string | Dayjs, isEnd = false): number => {
  const [hours, minutes] =
    typeof value === 'string'
      ? value.split(':').map(Number)
      : [value.hour(), value.minute()];
  const total = hours * 60 + minutes;
  return isEnd && total === 0 ? DAY_MINUTES : total;
};

export const minutesToPercent = (minutes: number): string =>
  `${(minutes / DAY_MINUTES) * 100}%`;

export const minutesToDayjs = (minutes: number): Dayjs =>
  dayjs()
    .startOf('day')
    .add(minutes % DAY_MINUTES, 'minute');

/** 12-hour format like the time pickers; the end of the day is 12:00 AM */
export const formatMinutes = (minutes: number): string =>
  minutesToDayjs(minutes).format('hh:mm A');

export const formatRange = (
  { start, end }: MinutesRange,
  t: TFunction
): string =>
  start === 0 && end === DAY_MINUTES
    ? t('digital_signage_schedule_all_day')
    : `${formatMinutes(start)} – ${formatMinutes(end)}`;

/** e.g. 30m, 2h, 1h 50m */
export const formatDuration = (minutes: number, t: TFunction): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours)
    return t('digital_signage_schedule_duration_minutes', { value: rest });
  return rest
    ? t('digital_signage_schedule_duration_hours_minutes', {
        hours,
        minutes: rest
      })
    : t('digital_signage_schedule_duration_hours', { value: hours });
};

/** Half-open check: 10:00–11:00 and 11:00–12:00 don't overlap */
export const doRangesOverlap = (a: MinutesRange, b: MinutesRange): boolean =>
  a.start < b.end && b.start < a.end;

/** Union of the ranges, sorted by start */
export const mergeRanges = (ranges: MinutesRange[]): MinutesRange[] =>
  [...ranges]
    .sort((a, b) => a.start - b.start)
    .reduce<MinutesRange[]>((merged, range) => {
      const last = merged[merged.length - 1];
      if (last && range.start <= last.end)
        last.end = Math.max(last.end, range.end);
      else merged.push({ ...range });
      return merged;
    }, []);

/** Free gaps of the day not covered by the ranges */
export const invertRanges = (ranges: MinutesRange[]): MinutesRange[] => {
  const free: MinutesRange[] = [];
  let cursor = 0;
  mergeRanges(ranges).forEach(({ start, end }) => {
    if (start > cursor) free.push({ start: cursor, end: start });
    cursor = Math.max(cursor, end);
  });
  if (cursor < DAY_MINUTES) free.push({ start: cursor, end: DAY_MINUTES });
  return free;
};

/** Overlapping parts of two range lists */
export const intersectRanges = (
  a: MinutesRange[],
  b: MinutesRange[]
): MinutesRange[] =>
  a.flatMap((x) =>
    b
      .filter((y) => doRangesOverlap(x, y))
      .map((y) => ({
        start: Math.max(x.start, y.start),
        end: Math.min(x.end, y.end)
      }))
  );

/** A day split into non-overlapping legend parts, sorted by time */
export const getDayLegendSegments = (
  booked: MinutesRange[],
  own: MinutesRange[]
): (MinutesRange & { type: ScheduleLegendEnum })[] => {
  const mergedBooked = mergeRanges(booked);
  const mergedOwn = mergeRanges(own);
  const parts: [ScheduleLegendEnum, MinutesRange[]][] = [
    [ScheduleLegendEnum.Free, invertRanges([...mergedBooked, ...mergedOwn])],
    [
      ScheduleLegendEnum.Booked,
      intersectRanges(mergedBooked, invertRanges(mergedOwn))
    ],
    [
      ScheduleLegendEnum.ThisSchedule,
      intersectRanges(mergedOwn, invertRanges(mergedBooked))
    ],
    [ScheduleLegendEnum.Conflict, intersectRanges(mergedOwn, mergedBooked)]
  ];

  return parts
    .flatMap(([type, ranges]) => ranges.map((range) => ({ ...range, type })))
    .filter((segment) => segment.end > segment.start)
    .sort((a, b) => a.start - b.start);
};

export const isTimeDisabled = (
  minutes: number,
  edge: TimeRangeEdge,
  start: number | null,
  slots: MinutesRange[]
): boolean => {
  if (edge === TimeRangeEdgeEnum.Start)
    return slots.some((s) => s.start <= minutes && minutes < s.end);

  const end = minutes || DAY_MINUTES;
  return start === null
    ? slots.some((s) => s.start < end && end <= s.end)
    : end <= start || slots.some((s) => doRangesOverlap(s, { start, end }));
};
